import React, { useState, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import ReactSwitch from 'react-switch';
import homepageSettingsApi from '../../api/homepageSettings';
import { toastError, toastSuccess } from '../../common/UI/Toasts';

const LandingPageSettings = ({ show, onHide, onSettingsUpdate }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [landingPageEnabled, setLandingPageEnabled] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#ffffff');
  const [colorTarget, setColorTarget] = useState(null);

  useEffect(() => {
    if (show) {
      fetchSettings();
    }
  }, [show]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await homepageSettingsApi.getSettings();
      const data = response?.data?.data;
      setSettings(data);
      setLandingPageEnabled(data?.landingPageEnabled || false);
    } catch (error) {
      toastError('Failed to fetch settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLandingPage = async () => {
    try {
      setLoading(true);
      const newStatus = !landingPageEnabled;
      await homepageSettingsApi.toggleLandingPage(newStatus);
      setLandingPageEnabled(newStatus);
      toastSuccess(`Landing page ${newStatus ? 'enabled' : 'disabled'} successfully`);
      if (onSettingsUpdate) onSettingsUpdate();
    } catch (error) {
      toastError('Failed to toggle landing page');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color.hex);
  };

  const handleColorSelect = async () => {
    if (!colorTarget) return;

    try {
      setLoading(true);
      const { section, property } = colorTarget;

      if (section === 'global') {
        const globalTheme = {
          ...settings?.theme?.global,
          [property]: selectedColor
        };
        await homepageSettingsApi.updateGlobalTheme(globalTheme);
      } else {
        const sectionTheme = {
          ...settings?.theme?.sections?.[section],
          [property]: selectedColor
        };
        await homepageSettingsApi.updateSectionTheme(section, sectionTheme);
      }

      toastSuccess('Theme updated successfully');
      setShowColorPicker(false);
      fetchSettings();
    } catch (error) {
      toastError('Failed to update theme');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openColorPicker = (section, property, currentColor) => {
    setColorTarget({ section, property });
    setSelectedColor(currentColor || '#ffffff');
    setShowColorPicker(true);
  };

  const handleResetTheme = async () => {
    if (!window.confirm('Are you sure you want to reset all theme settings to defaults?')) {
      return;
    }

    try {
      setLoading(true);
      await homepageSettingsApi.resetTheme();
      toastSuccess('Theme reset to defaults successfully');
      fetchSettings();
    } catch (error) {
      toastError('Failed to reset theme');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-cog me-2"></i>
            Landing Page Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Landing Page Toggle */}
          <div className="mb-4 p-4 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">
                  <i className="fa fa-power-off me-2 text-primary"></i>
                  New Landing Page
                </h5>
                <p className="text-muted mb-0">
                  Enable the new landing page design with CMS-managed sections
                </p>
              </div>
              <ReactSwitch
                uncheckedIcon={false}
                checkedIcon={false}
                onChange={handleToggleLandingPage}
                checked={landingPageEnabled}
                height={28}
                width={56}
                onColor="#28a745"
                offColor="#6c757d"
                disabled={loading}
              />
            </div>
            {landingPageEnabled && (
              <div className="mt-3 pt-3 border-top">
                <span className="badge bg-success">
                  <i className="fa fa-check-circle me-1"></i>
                  New landing page is active
                </span>
              </div>
            )}
          </div>

          {/* Global Theme Settings */}
          <div className="mb-4">
            <h6 className="mb-3">
              <i className="fa fa-palette me-2"></i>
              Global Theme Colors
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                  <span>Primary Color</span>
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: settings?.theme?.global?.primaryColor || '#007bff',
                      width: '60px',
                      height: '30px',
                      border: '1px solid #dee2e6'
                    }}
                    onClick={() => openColorPicker('global', 'primaryColor', settings?.theme?.global?.primaryColor)}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                  <span>Secondary Color</span>
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: settings?.theme?.global?.secondaryColor || '#6c757d',
                      width: '60px',
                      height: '30px',
                      border: '1px solid #dee2e6'
                    }}
                    onClick={() => openColorPicker('global', 'secondaryColor', settings?.theme?.global?.secondaryColor)}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                  <span>Success Color</span>
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: settings?.theme?.global?.successColor || '#28a745',
                      width: '60px',
                      height: '30px',
                      border: '1px solid #dee2e6'
                    }}
                    onClick={() => openColorPicker('global', 'successColor', settings?.theme?.global?.successColor)}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                  <span>Danger Color</span>
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: settings?.theme?.global?.dangerColor || '#dc3545',
                      width: '60px',
                      height: '30px',
                      border: '1px solid #dee2e6'
                    }}
                    onClick={() => openColorPicker('global', 'dangerColor', settings?.theme?.global?.dangerColor)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section-specific Theme Settings */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="fa fa-paint-brush me-2"></i>
                Section Themes
              </h6>
              <Button variant="outline-danger" size="sm" onClick={handleResetTheme} disabled={loading}>
                <i className="fa fa-undo me-1"></i>
                Reset All
              </Button>
            </div>
            <div className="alert alert-info">
              <i className="fa fa-info-circle me-2"></i>
              Click on any section in the main list to customize its individual theme colors
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Color Picker Modal */}
      <Modal show={showColorPicker} onHide={() => setShowColorPicker(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Color</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center">
          <SketchPicker
            color={selectedColor}
            onChangeComplete={handleColorChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColorPicker(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleColorSelect}>
            Apply Color
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LandingPageSettings;